import { useContext, useEffect, useEffectEvent, useRef } from "react";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { SUPERSET_LOCALE_URL_PARAM, useGetEmbeddedSupersetData } from "src/api/superset.ts";
import { resolveRequestLocale } from "src/api/axiosClient/userApi";
import { Loading } from "src/components/utils/Loading.tsx";
import { useTranslation } from "react-i18next";
import EmptyStateCard from "src/components/EmptyState/EmptyStateCard";
import { WhiteBox } from "@foodpilot/foods";
import { useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import isAppInPDFMode from "src/utils/rendering.ts";
import { RootPropsContext } from "src/components/Puck/config/root.tsx";
import { watchChartsDataEmptiness } from "src/components/Superset/watchChartsDataEmptiness.ts";

type SupersetDashboardEmbedProps = {
    dashboardUuid: string;
    hideTitle: boolean;
    hideTab: boolean;
    hideChartControls: boolean;
    filtersVisible: boolean;
    hideWhenEmpty?: boolean;
    onEmptyChange?: (isEmpty: boolean) => void;
};

export const SupersetDashboardEmbed = (props: SupersetDashboardEmbedProps) => {
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const rootProps = useContext(RootPropsContext);

    const {
        dashboardUuid,
        hideTitle = true,
        hideTab = true,
        hideChartControls = false,
        filtersVisible = true,
        hideWhenEmpty = false,
        onEmptyChange,
    } = props;
    const { t, i18n } = useTranslation();
    const supersetLocale = resolveRequestLocale(undefined, i18n.resolvedLanguage);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const isPdfMode = rootProps?.pageType === "PDF" || isAppInPDFMode();

    const {
        data: embeddedData,
        isLoading,
        isError,
    } = useGetEmbeddedSupersetData({
        resources: [{ type: "dashboard", id: dashboardUuid }],
    });

    const reportEmpty = useEffectEvent(() => {
        onEmptyChange?.(true);
    });

    useEffect(() => {
        if (isLoading || !embeddedData || !containerRef.current) {
            return;
        }

        const mountPoint = containerRef.current;
        mountPoint.innerHTML = "";

        let destroyed = false;
        let cleanupResize: (() => void) | null = null;
        let cleanupUnmount: (() => void) | null = null;

        const backendUrlParams = embeddedData.urlParams;
        const finalUrlParams = {
            user_id: backendUrlParams.user_id,
            selected_organization_id: selectedCompany?.id,
            selected_campaign_id: currentCampaign?.id,
            [SUPERSET_LOCALE_URL_PARAM]: supersetLocale,
        };

        let isAwaitingEmptiness = hideWhenEmpty;
        let revealIframe: (() => void) | null = null;

        const stopWatchingEmptiness =
            hideWhenEmpty ?
                watchChartsDataEmptiness({
                    origin: new URL(embeddedData.hostUrl).origin,
                    getWindow: () => mountPoint.querySelector("iframe")?.contentWindow ?? null,
                    onDecision: (isEmpty) => {
                        if (destroyed) {
                            return;
                        }

                        if (isEmpty === true) {
                            reportEmpty();
                            return;
                        }

                        isAwaitingEmptiness = false;
                        revealIframe?.();
                    },
                })
            :   null;

        void (async () => {
            let embeddedDashboard: Awaited<ReturnType<typeof embedDashboard>>;

            try {
                embeddedDashboard = await embedDashboard({
                    id: dashboardUuid,
                    supersetDomain: embeddedData.hostUrl,
                    mountPoint,
                    fetchGuestToken: async () => {
                        return embeddedData.guestToken.guestToken;
                    },
                    dashboardUiConfig: {
                        hideTitle: hideTitle,
                        hideTab: hideTab,
                        hideChartControls: hideChartControls,
                        urlParams: finalUrlParams,
                        filters: {
                            visible: filtersVisible,
                            expanded: filtersVisible,
                        },
                    },
                });
            } catch {
                return;
            }

            if (destroyed) {
                embeddedDashboard.unmount();
                return;
            }

            cleanupUnmount = embeddedDashboard.unmount;

            const iframe = mountPoint.querySelector("iframe");
            if (!iframe) {
                return;
            }

            mountPoint.style.width = "100%";
            mountPoint.style.maxWidth = "100%";
            mountPoint.style.display = "block";

            iframe.style.width = "100%";
            iframe.style.maxWidth = "100%";
            iframe.style.border = "none";
            iframe.style.display = "block";

            const getHostWidth = (): number => {
                const mountRectWidth = mountPoint.getBoundingClientRect().width;
                if (mountRectWidth > 0) {
                    return mountRectWidth;
                }

                const parentRectWidth = mountPoint.parentElement?.getBoundingClientRect().width ?? 0;
                if (parentRectWidth > 0) {
                    return parentRectWidth;
                }

                return window.innerWidth;
            };

            const applyIframeWidth = (measuredWidth?: number): number => {
                const hostWidth = getHostWidth();
                const clampedWidth = Math.max(1, Math.floor(Math.min(measuredWidth ?? hostWidth, hostWidth)));
                iframe.style.width = `${clampedWidth}px`;
                return clampedWidth;
            };

            const initialWidth = applyIframeWidth();
            const fallbackHeight = Math.max(320, Math.round(initialWidth * 0.6));
            const measurementHeight = 1;
            let isSized = false;

            const prepareIframeMeasurement = () => {
                isSized = false;
                iframe.style.height = `${measurementHeight}px`;
                iframe.style.visibility = "hidden";
            };

            const showIframe = (height: number) => {
                isSized = true;
                iframe.style.height = `${height}px`;
                iframe.style.visibility = isAwaitingEmptiness ? "hidden" : "visible";
                mountPoint.style.height = `${height}px`;
                mountPoint.style.minHeight = "0";
            };

            prepareIframeMeasurement();

            revealIframe = () => {
                if (isSized) {
                    iframe.style.visibility = "visible";
                }
            };

            if (isPdfMode) {
                iframe.style.overflow = "hidden";
                iframe.setAttribute("scrolling", "no");
            }

            const measureTimeoutMs = 2000;

            const updateIframeSize = async (): Promise<number | null> => {
                applyIframeWidth();

                let timeoutId: number | undefined;
                let size: { width: number; height: number } | null;

                try {
                    size = await Promise.race([
                        embeddedDashboard.getScrollSize(),
                        new Promise<never>((_, reject) => {
                            timeoutId = window.setTimeout(() => {
                                reject(new Error("getScrollSize timed out"));
                            }, measureTimeoutMs);
                        }),
                    ]);
                } catch {
                    size = null;
                } finally {
                    window.clearTimeout(timeoutId);
                }

                if (destroyed || size === null) {
                    if (!destroyed && !isSized) {
                        showIframe(fallbackHeight);
                    }
                    return null;
                }

                if (size.width > 0) {
                    applyIframeWidth(size.width);
                }

                if (size.height > measurementHeight) {
                    const nextHeight = Math.ceil(size.height);
                    showIframe(nextHeight);
                    return nextHeight;
                }

                return null;
            };

            let resizeTimeoutId: number | null = null;
            const onResize = () => {
                if (resizeTimeoutId !== null) {
                    window.clearTimeout(resizeTimeoutId);
                }
                resizeTimeoutId = window.setTimeout(() => {
                    prepareIframeMeasurement();
                    void updateIframeSize();
                }, 200);
            };

            window.addEventListener("resize", onResize);
            cleanupResize = () => {
                window.removeEventListener("resize", onResize);
                if (resizeTimeoutId !== null) {
                    window.clearTimeout(resizeTimeoutId);
                }
            };

            const startMeasureLoop = () => {
                let rafId: number | null = null;
                let inFlight = false;
                let measurements = 0;
                let lastMeasuredAt = 0;
                const minIntervalMs = 200;
                const maxMeasurements = 60;

                const tick = () => {
                    if (destroyed) {
                        return;
                    }

                    const now = performance.now();

                    if (inFlight || now - lastMeasuredAt < minIntervalMs) {
                        rafId = window.requestAnimationFrame(tick);
                        return;
                    }

                    inFlight = true;
                    lastMeasuredAt = now;
                    measurements += 1;

                    void updateIframeSize().finally(() => {
                        inFlight = false;

                        if (destroyed || measurements >= maxMeasurements) {
                            if (!destroyed && !isSized) {
                                showIframe(fallbackHeight);
                            }
                            return;
                        }

                        rafId = window.requestAnimationFrame(tick);
                    });
                };

                rafId = window.requestAnimationFrame(tick);

                return () => {
                    if (rafId !== null) {
                        window.cancelAnimationFrame(rafId);
                    }
                };
            };

            const cleanupMeasureLoop = startMeasureLoop();
            const loadTimeouts: number[] = [];

            const onLoad = () => {
                prepareIframeMeasurement();
                void updateIframeSize();

                loadTimeouts.push(
                    window.setTimeout(() => {
                        void updateIframeSize();
                    }, 250),
                );

                loadTimeouts.push(
                    window.setTimeout(() => {
                        void updateIframeSize();
                    }, 1000),
                );
            };

            iframe.addEventListener("load", onLoad);

            const previousCleanupResize = cleanupResize;
            cleanupResize = () => {
                previousCleanupResize?.();
                cleanupMeasureLoop?.();
                iframe.removeEventListener("load", onLoad);

                for (const timeoutId of loadTimeouts) {
                    window.clearTimeout(timeoutId);
                }
            };
        })();

        return () => {
            destroyed = true;
            stopWatchingEmptiness?.();
            cleanupResize?.();
            cleanupUnmount?.();
        };
    }, [
        currentCampaign?.id,
        dashboardUuid,
        embeddedData,
        filtersVisible,
        hideChartControls,
        hideTab,
        hideTitle,
        hideWhenEmpty,
        isLoading,
        isPdfMode,
        selectedCompany?.id,
        supersetLocale,
    ]);

    if (isLoading) {
        return <Loading />;
    }

    if (isError) {
        return (
            <EmptyStateCard
                title={t("common.error_occurred", "An error occurred")}
                description={t("superset.component.dashboard.invalid_uuid")}
            />
        );
    }

    return (
        <WhiteBox
            sx={{
                width: "100%",
                maxWidth: "100%",
                overflow: "visible",
            }}
        >
            <div
                ref={containerRef}
                style={{
                    height: "auto",
                    minHeight: "320px",
                    width: "100%",
                }}
            />
        </WhiteBox>
    );
};

export default SupersetDashboardEmbed;
